import React, { useState } from "react";
import { connect } from "react-redux";
import { Modal, Form, Input, Button, Card } from "antd";
import { MailOutlined, LockOutlined } from "@ant-design/icons";
import JwtAuthService from "services/JwtAuthService";
import otpService from "services/OTPService";
import {
	authenticated,
	sessionExpiredCleared,
	signOut,
} from "redux/actions/Auth";
import { SESSION_EXPIRED_REAUTH_KEY } from "redux/constants/Auth";
import { TipoOtpEnum } from "enum/tipo-otp-enum";
import { useHistory } from "react-router-dom";
import { AUTH_PREFIX_PATH, PUBLIC_PREFIX_PATH } from "configs/AppConfig";

const ReAuthModal = ({
	visible,
	authenticated,
	sessionExpiredCleared,
	signOut,
}) => {
	const history = useHistory();
	const [form] = Form.useForm();
	const [otpForm] = Form.useForm();
	const [loading, setLoading] = useState(false);
	const [otpStep, setOtpStep] = useState(false);
	const [otpLoginCode, setOtpLoginCode] = useState(null);
	const [otpTipo, setOtpTipo] = useState(null);

	const handleLoginSuccess = (token) => {
		authenticated(token);
		sessionExpiredCleared();
		sessionStorage.removeItem(SESSION_EXPIRED_REAUTH_KEY);
		setOtpStep(false);
		form.resetFields();
		otpForm.resetFields();
	};

	const onLoginFinish = async (values) => {
		setLoading(true);
		try {
			const resp = await JwtAuthService.login(values.email, values.password);
			if (resp.otp_habilitado) {
				setOtpLoginCode(resp.otp_login_code);
				setOtpTipo(resp.otp_tipo);
				setOtpStep(true);
			} else {
				handleLoginSuccess(`${resp.token_type} ${resp.token}`);
			}
		} catch (err) {
			// Toast da aplicação já exibe "usuário ou senha inválidos"
		} finally {
			setLoading(false);
		}
	};

	const onOtpFinish = async (values) => {
		setLoading(true);
		try {
			const res = await otpService.verificarOtp(otpLoginCode, values.otp);
			handleLoginSuccess(`${res.token_type} ${res.token}`);
		} catch (err) {
			// Toast da aplicação já exibe a mensagem de erro
		} finally {
			setLoading(false);
		}
	};

	const handleSairParaLogin = () => {
		sessionExpiredCleared();
		sessionStorage.removeItem(SESSION_EXPIRED_REAUTH_KEY);
		signOut();
		history.push(`${AUTH_PREFIX_PATH}/login`);
	};

	const handleBackToLoginStep = () => {
		setOtpStep(false);
		setOtpLoginCode(null);
		setOtpTipo(null);
		otpForm.resetFields();
	};

	return (
		<Modal
			visible={visible}
			closable={false}
			maskClosable={false}
			keyboard={false}
			footer={null}
			width={480}
			centered
		>
			<Card
				style={{ marginBottom: 16 }}
				bodyStyle={{ padding: "8px 12px" }}
			>
				<p
					style={{
						margin: 0,
						textAlign: "center",
						fontSize: 11,
						color: "rgba(0, 0, 0, 0.65)",
						lineHeight: 1.4,
					}}
				>
					Sua sessão expirou após 30 minutos de inatividade, por favor,
					realize o login novamente para continuar.
				</p>
			</Card>
			<div className="text-center" style={{ marginBottom: 16 }}>
				<img
					className="img-fluid"
					src={`${PUBLIC_PREFIX_PATH}/tebex.png`}
					alt="Logo"
					style={{ maxWidth: 180 }}
				/>
			</div>

			{!otpStep ? (
				<Form
					form={form}
					layout="vertical"
					name="reauth-login-form"
					onFinish={onLoginFinish}
				>
					<Form.Item
						name="email"
						label="E-mail"
						rules={[
							{ required: true, message: "Esse campo é obrigatório" },
							{ type: "email", message: "Por favor, insira um e-mail válido!" },
							{
								validator(_, value) {
									if (value && /[A-Z]/.test(value)) {
										return Promise.reject(
											"Por favor, insira o e-mail apenas com letras minúsculas!",
										);
									}
									return Promise.resolve();
								},
							},
						]}
					>
						<Input prefix={<MailOutlined className="text-primary" />} />
					</Form.Item>
					<Form.Item
						name="password"
						label="Senha"
						rules={[{ required: true, message: "Esse campo é obrigatório" }]}
					>
						<Input.Password
							prefix={<LockOutlined className="text-primary" />}
						/>
					</Form.Item>
					<Form.Item>
						<Button type="primary" htmlType="submit" block loading={loading}>
							Entrar
						</Button>
					</Form.Item>
				</Form>
			) : (
				<Form
					form={otpForm}
					layout="vertical"
					name="reauth-otp-form"
					onFinish={onOtpFinish}
				>
					<div style={{ marginBottom: 16, textAlign: "center" }}>
						{otpTipo === TipoOtpEnum.APP.toString() && (
							<p>
								Utilize o app <b>Google Authenticator</b> para obter o código.
							</p>
						)}
						{otpTipo === TipoOtpEnum.EMAIL.toString() && (
							<p>Verifique seu e-mail para obter o código de autenticação.</p>
						)}
					</div>
					<Form.Item
						name="otp"
						label="Código"
						rules={[{ required: true, message: "Esse campo é obrigatório" }]}
					>
						<Input placeholder="Digite o código" />
					</Form.Item>
					<Form.Item>
						<div style={{ display: "flex", gap: 8 }}>
							<Button onClick={handleBackToLoginStep} block>
								Voltar
							</Button>
							<Button type="primary" htmlType="submit" block loading={loading}>
								Confirmar
							</Button>
						</div>
					</Form.Item>
				</Form>
			)}

			<div style={{ marginTop: 16, textAlign: "center" }}>
				<a
					onClick={handleSairParaLogin}
					className="cursor-pointer font-size-sm text-muted"
				>
					Sair e ir para a página de login
				</a>
			</div>
		</Modal>
	);
};

const mapDispatchToProps = {
	authenticated,
	sessionExpiredCleared,
	signOut,
};

export default connect(null, mapDispatchToProps)(ReAuthModal);
